import { memo } from 'react';

import { Divider, Flex, Spacer } from '@zillow/constellation';
import { Text, TextButton } from '@zillow/constellation-10';
import type {
  ExistingFeesDto,
  FeeCategoryDictionaryItemDto,
  GetSingleFamilyFeesDictionaryItemsDto,
  OtherFeesPseudoCategoryDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/rental-manager-env';

import { getIconByName } from '../../../../../shared/utils/iconUtils';
import { TRIGGER_LOCATIONS_MAP } from '../constants';

import CostsAndFeesSFCSEvents from '../../../../model/ClickstreamEvent/CostsAndFeesSFCSEvents';

import { FeeListItem } from './FeeListItem';

type Props = {
  dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto;
  existingFees: ExistingFeesDto;
  handleAddFeeClick: (fixedCategory?: string) => void;
  handleEditFeeClick: (fee: SingleFamilyFeeDto) => void;
  displayedLocation: string;
};

// TODO: adjust the width of the display to match figma
export const FeesByCategory = memo(
  ({ dictionaryItems, existingFees, handleAddFeeClick, handleEditFeeClick, displayedLocation }: Props) => {
    const getFeeMainCategoryDisplay = (
      feeCategoryKey: string,
      dictionaryItems: Record<string, FeeCategoryDictionaryItemDto>,
    ) => {
      const { displayName, feeCategoryIconName } = dictionaryItems[feeCategoryKey];
      const Icon = getIconByName({ name: feeCategoryIconName, useFallbackIcon: true });
      return (
        <Flex paddingY="sm" display="flex" flexDirection="row" justifyContent="space-between">
          <Flex display="flex" flexDirection="row" gap="sm">
            <Icon size="sm" />
            <Text textStyle="body-bold">{displayName}</Text>
          </Flex>
          <TextButton
            onClick={() => handleAddFeeClick(feeCategoryKey)}
            data-testid={`CostsAndFees-Add${displayName}FeeButton`}
            data-eventid={CostsAndFeesSFCSEvents.ClickAddFee.data_event_id}
          >
            Add
          </TextButton>
        </Flex>
      );
    };

    const getFeeOtherCategoryDisplay = (dictionaryItems: OtherFeesPseudoCategoryDto) => {
      const { displayName, feeCategoryIconName } = dictionaryItems;
      const Icon = getIconByName({ name: feeCategoryIconName, useFallbackIcon: true });
      return (
        <Flex paddingY="sm" display="flex" flexDirection="row" justifyContent="space-between">
          <Flex display="flex" flexDirection="row" gap="sm">
            <Icon size="sm" />
            <Text textStyle="body-bold">{displayName}</Text>
          </Flex>
          <TextButton
            onClick={() => handleAddFeeClick()}
            data-eventid={CostsAndFeesSFCSEvents.ClickAddFee.data_event_id}
          >
            Add
          </TextButton>
        </Flex>
      );
    };

    return (
      <div data-testid="SingleFamilyFeesByCategory">
        {Object.keys(dictionaryItems.mainCategories).map((categoryKey) => (
          <Spacer key={categoryKey}>
            {getFeeMainCategoryDisplay(categoryKey, dictionaryItems.mainCategories)}

            <Divider />
            {existingFees.mainCategories[categoryKey as keyof typeof existingFees.mainCategories]?.map(
              (fee: SingleFamilyFeeDto) => (
                <Spacer
                  key={fee.costAndFeeId}
                  marginLeft={displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? 'lg' : 'none'}
                >
                  <FeeListItem
                    fee={fee}
                    dictionaryItems={dictionaryItems}
                    handleEditFeeClick={handleEditFeeClick}
                    displayedLocation={displayedLocation}
                  />
                </Spacer>
              ),
            )}
          </Spacer>
        ))}
        <Spacer key={'otherCategories'}>
          {getFeeOtherCategoryDisplay(dictionaryItems.otherCategories)}

          <Divider />
          {existingFees.otherCategories.map((fee: SingleFamilyFeeDto) => (
            <Spacer
              key={fee.costAndFeeId}
              marginLeft={displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? 'lg' : 'none'}
            >
              <FeeListItem
                fee={fee}
                dictionaryItems={dictionaryItems}
                handleEditFeeClick={handleEditFeeClick}
                displayedLocation={displayedLocation}
              />
            </Spacer>
          ))}
        </Spacer>
      </div>
    );
  },
);

FeesByCategory.displayName = 'FeesByCategory';
